// TodayChairsView — the "Today's chairs" panel (C4, bead jonathan-fym6).
//
// Faithful port of the "Today's chairs" column in Jonathan.dc.html: a cream
// panel of timed rows — big time + AM/PM, an accent left-rule, guest name,
// "service · stylist", and a status dot (filled circle with a glyph) + note.
// Dossier-linked rows (the booking joined to a widget conversation thread) read
// the confirm-in-chair line — e.g. "Base booked · you confirm color in chair" —
// and are tappable: clicking opens the C3 guest dossier drawer on that thread.
//
// Wired to GET /v1/owner/today-chairs (jonathan/http/owner/today_chairs_api.py):
//   {
//     square_connected: bool,
//     chairs: [ {
//       booking_id, start_at (ISO-8601 UTC 'Z'), guest_name, stylist_name,
//       service_name, status, thread_id|null,
//       stylist_prep?: render_stylist_prep(...)   // present iff a live dossier
//     } ]
//   }
//
// This endpoint is built-as-code but not deployed yet (deploy bead h07w), so the
// view degrades gracefully: loading / not-connected / empty / error — it never
// crashes on a 404.
//
// LOCKED DECISIONS honored: NO numeric confidence anywhere (the confirm-in-chair
// note is the qualitative fold-into-appointment signal, not a score). Plain
// language. No quick-reply chips. The confirm-in-chair line is surfaced as a
// stylist heads-up, never a client gate. Clean single-path component.

import { useEffect, useState, useCallback } from 'preact/hooks';
import { apiFetch, ApiError } from '../api/client';
import { ASSISTANT_NAME } from '../brand.js';

// --- time -------------------------------------------------------------------
// start_at is an ISO-8601 UTC 'Z' string. The Square handler already scoped the
// window to the salon-local day, so formatting in the browser's locale renders
// the wall-clock time. We split into the bold hour:minute and the small AM/PM
// to match the .dc.html ("10" + "AM", "1:30" + "PM").
function formatTime(startAt) {
  if (!startAt) return { time: '', ampm: '' };
  const d = new Date(startAt);
  if (Number.isNaN(d.getTime())) return { time: '', ampm: '' };
  let h = d.getHours();
  const m = d.getMinutes();
  const ampm = h >= 12 ? 'PM' : 'AM';
  h = h % 12;
  if (h === 0) h = 12;
  const time = m === 0 ? `${h}` : `${h}:${String(m).padStart(2, '0')}`;
  return { time, ampm };
}

// --- status -----------------------------------------------------------------
// The .dc.html row foot is a filled status dot (glyph) + a note line. A booking
// joined to a dossier whose service_map folds color into the appointment reads
// the olive confirm-in-chair line; a plain confirmed booking reads the calm
// taupe "You confirmed this". Cancelled/declined read terracotta. Colors are
// lifted verbatim from the design (accent / dot / noteColor).
function deriveStatus(chair) {
  const status = (chair.status || '').toUpperCase();

  if (status === 'CANCELLED_BY_CUSTOMER' || status === 'CANCELLED_BY_SELLER' || status === 'DECLINED') {
    return {
      accent: '#D8BCB0',
      dot: '#C5512F',
      glyph: '✕',
      note: 'Cancelled',
      noteColor: '#9A5A45',
      source: 'square',
    };
  }

  // A dossier-linked booking whose service_map folds the rest into the
  // appointment is the "base booked · you confirm color in chair" case.
  const prep = chair.stylist_prep;
  const folds =
    prep &&
    Array.isArray(prep.service_map) &&
    prep.service_map.some((m) => m && m.fold_into_appointment);
  if (folds) {
    return {
      accent: '#56683F',
      dot: '#56683F',
      glyph: '✓',
      note: 'Base booked · you confirm color in chair',
      noteColor: '#3C4730',
      source: 'jonathan',
    };
  }

  // Any other dossier-linked booking — the dossier is ready, calmly olive.
  if (chair.thread_id && prep) {
    return {
      accent: '#56683F',
      dot: '#56683F',
      glyph: '✓',
      note: 'Dossier ready · tap to read',
      noteColor: '#3C4730',
      source: 'jonathan',
    };
  }

  // hy3u — single pane of glass: an appointment with no widget conversation
  // behind it was booked externally (walk-in, phone, or directly in Square).
  // Give it a DISTINCT treatment (periwinkle + "Booked in Square") so the owner
  // sees their WHOLE day, not just Jonathan-touched bookings — replaces the old
  // misleading "You confirmed this".
  return {
    accent: '#8D9FF1',
    dot: '#6F83E8',
    glyph: '✓',
    note: 'Booked in Square',
    noteColor: '#5A67B0',
    source: 'square',
  };
}

function ChairRow({ chair, isLast, onOpenGuest }) {
  const { time, ampm } = formatTime(chair.start_at);
  const s = deriveStatus(chair);
  const name = chair.guest_name || 'Guest';
  const service = chair.service_name || 'Appointment';
  const stylist = chair.stylist_name;
  // Only dossier-linked rows are tappable (they open the C3 drawer on the
  // joined thread). Plain rows render with a default cursor — matches the
  // .dc.html cursor:default vs cursor:pointer split.
  const tappable = Boolean(chair.thread_id);

  return (
    <div
      onClick={tappable ? () => onOpenGuest?.(chair.thread_id) : undefined}
      style={{
        display: 'flex',
        gap: '16px',
        padding: '18px 0',
        borderBottom: isLast ? 'none' : '1px solid rgba(34,30,23,.07)',
        cursor: tappable ? 'pointer' : 'default',
      }}
    >
      <div style={{ flex: 'none', width: '58px', textAlign: 'right', paddingTop: '1px' }}>
        <div style={{ fontSize: '15px', fontWeight: 800, color: 'var(--ink)' }}>{time}</div>
        <div style={{ fontSize: '11.5px', fontWeight: 600, color: 'var(--ink-faint)' }}>{ampm}</div>
      </div>
      <div
        style={{
          flex: 1,
          minWidth: 0,
          borderLeft: `2px solid ${s.accent}`,
          paddingLeft: '15px',
        }}
      >
        <div style={{ fontSize: '15px', fontWeight: 700, color: 'var(--ink)', marginBottom: '2px' }}>
          {name}
        </div>
        <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--ink-muted)', marginBottom: '9px' }}>
          {service}
          {stylist ? ` · ${stylist}` : ''}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
          <span
            style={{
              flex: 'none',
              width: '14px',
              height: '14px',
              borderRadius: '50%',
              background: s.dot,
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '9px',
              fontWeight: 800,
            }}
          >
            {s.glyph}
          </span>
          <span style={{ fontSize: '12.5px', fontWeight: 600, color: s.noteColor, lineHeight: 1.35 }}>
            {s.note}
          </span>
          {s.source && (
            <span
              style={{
                fontSize: '9.5px',
                fontWeight: 800,
                letterSpacing: '.04em',
                textTransform: 'uppercase',
                padding: '2px 7px',
                borderRadius: '100px',
                marginLeft: '2px',
                background: s.source === 'jonathan' ? '#E5EAD6' : '#E6EBFF',
                color: s.source === 'jonathan' ? '#33401E' : '#4655A8',
              }}
            >
              {s.source === 'jonathan' ? ASSISTANT_NAME : 'Square'}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

export function TodayChairsView({ onOpenGuest, onTeachJonathan, headerSlot }) {
  const [state, setState] = useState('loading'); // loading | ready | not_connected | empty | error
  const [chairs, setChairs] = useState([]);

  const load = useCallback(async () => {
    setState('loading');
    try {
      const data = await apiFetch('/v1/owner/today-chairs');
      if (data && data.square_connected === false) {
        setChairs([]);
        setState('not_connected');
        return;
      }
      const rows = Array.isArray(data && data.chairs) ? data.chairs : [];
      setChairs(rows);
      setState(rows.length === 0 ? 'empty' : 'ready');
    } catch (err) {
      // 404 = endpoint not deployed yet (deploy bead h07w); degrade calmly
      // rather than crash. Any other non-2xx -> a quiet error panel.
      if (err instanceof ApiError && err.status === 404) {
        setChairs([]);
        setState('empty');
      } else {
        setChairs([]);
        setState('error');
      }
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div
      class="view active fixed inset-0 overflow-y-auto"
      style={{
        background: 'var(--bg)',
        paddingBottom: 'calc(80px + var(--safe-area-bottom))',
      }}
    >
      <main
        class="desktop-container"
        style={{ padding: 'calc(40px + var(--safe-area-top)) 32px 80px' }}
      >
        {/* Optional masthead — the mobile Today header (C8) scrolls with the
            chairs in this one container, so the greeting/ROI block sits above
            the panel exactly as in Jonathan-Mobile.dc.html. Omitted on desktop,
            where the chairs panel stands alone. */}
        {headerSlot}
        <h2
          class="font-serif"
          style={{ fontWeight: 600, fontSize: '23px', color: 'var(--ink)', marginBottom: '16px' }}
        >
          Today's chairs
        </h2>

        {state === 'loading' && (
          <div
            class="card"
            style={{ textAlign: 'center', padding: '40px 24px', color: 'var(--ink-soft)' }}
          >
            <div style={{ fontSize: '13.5px', fontWeight: 600 }}>Reading today's book…</div>
          </div>
        )}

        {state === 'not_connected' && (
          <div class="card" style={{ textAlign: 'center', padding: '40px 24px' }}>
            <div
              class="font-serif"
              style={{ fontStyle: 'italic', fontSize: '18px', color: '#3A352C', marginBottom: '6px' }}
            >
              Connect Square to see today's chairs.
            </div>
            <div style={{ fontSize: '13.5px', fontWeight: 600, color: 'var(--ink-soft)' }}>
              Once your booking calendar is linked, every appointment shows up here.
            </div>
          </div>
        )}

        {state === 'empty' && (
          <div class="card" style={{ textAlign: 'center', padding: '40px 24px' }}>
            <div
              class="font-serif"
              style={{ fontStyle: 'italic', fontSize: '18px', color: '#3A352C', marginBottom: '6px' }}
            >
              No one in the chair today.
            </div>
            <div style={{ fontSize: '13.5px', fontWeight: 600, color: 'var(--ink-soft)' }}>
              When today's appointments come through, they land here.
            </div>
            {onTeachJonathan && (
              <div
                onClick={onTeachJonathan}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onTeachJonathan();
                  }
                }}
                style={{
                  marginTop: '20px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: '13.5px',
                  fontWeight: 700,
                  color: 'var(--olive)',
                  cursor: 'pointer',
                }}
              >
                Teach {ASSISTANT_NAME} about your salon
                <span aria-hidden="true">→</span>
              </div>
            )}
          </div>
        )}

        {state === 'error' && (
          <div class="card" style={{ textAlign: 'center', padding: '40px 24px' }}>
            <div
              class="font-serif"
              style={{ fontStyle: 'italic', fontSize: '18px', color: '#3A352C', marginBottom: '6px' }}
            >
              Couldn't load today's chairs.
            </div>
            <div
              onClick={load}
              style={{
                fontSize: '13px',
                fontWeight: 700,
                color: 'var(--olive)',
                cursor: 'pointer',
                marginTop: '4px',
              }}
            >
              Try again
            </div>
          </div>
        )}

        {state === 'ready' && (
          <div
            style={{
              background: 'var(--surface)',
              border: '1px solid var(--border-subtle)',
              borderRadius: '18px',
              padding: '6px 20px',
            }}
          >
            {chairs.map((chair, i) => (
              <ChairRow
                key={chair.booking_id || i}
                chair={chair}
                isLast={i === chairs.length - 1}
                onOpenGuest={onOpenGuest}
              />
            ))}
            <div
              style={{
                padding: '16px 0 14px',
                textAlign: 'center',
                fontSize: '12.5px',
                fontWeight: 600,
                color: 'var(--ink-faint)',
              }}
            >
              That's the day. Front desk never slept.
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
